// TemplateTreeSidebar — the Templates tab's binding of the shared
// ExplorerSidebar. Identical to the Dashboards binding but typed for
// `Template` / `TemplateFolder` with a `LayoutTemplate` leaf icon and the
// template action vocabulary, so the two tabs render through the same
// component and can't drift apart. The parent's props are unchanged —
// selection is mapped onto the shared `ExplorerSelection` here.

import { FolderInput, FolderPlus, LayoutTemplate, Pencil, Plus, Trash2 } from 'lucide-react'
import type { ReactNode } from 'react'
import type { Template, TemplateFolder } from '@axilon-apps/domains-ontology-ui-state'

import {
  ExplorerSidebar,
  type ExplorerMenuEntry,
  type ExplorerSelection,
  type ExplorerTreeNode
} from '../../components/explorer'
import { DASHBOARDS_EXPLORER_OPEN_KEY } from './explorer-paths'

export type TemplateSelection =
  | { kind: 'folder'; path: string }
  | { kind: 'template'; id: string }
  | { kind: 'none' }

/**
 * Discriminated union of every action the templates sidebar can request from
 * the parent page. Mirror of `DashboardSidebarAction` but with template-folder
 * / template payloads so the dispatch remains exhaustive in the parent's
 * switch.
 */
export type TemplateSidebarAction =
  | { kind: 'create-folder'; parentPath: string }
  | { kind: 'create-template'; parentPath: string }
  | { kind: 'rename-folder'; path: string; name: string; folder?: TemplateFolder }
  | { kind: 'rename-template'; template: Template }
  | { kind: 'move-folder'; path: string; name: string }
  | { kind: 'move-template'; template: Template }
  | { kind: 'drop-folder'; fromPath: string; toParentPath: string }
  | { kind: 'drop-template'; template: Template; toParentPath: string }
  | { kind: 'delete-folder'; path: string; name: string; folder?: TemplateFolder }
  | { kind: 'delete-template'; template: Template }

export interface TemplateTreeSidebarProps {
  folders: TemplateFolder[]
  templates: Template[]
  selection: TemplateSelection
  onSelectFolder: (path: string) => void
  onSelectTemplate: (id: string) => void
  expanded: Set<string>
  onToggle: (path: string) => void
  onAction: (action: TemplateSidebarAction) => void
  filter: string
  onFilterChange: (filter: string) => void
  isLoading?: boolean
  /**
   * Optional content rendered above the sidebar header — used by the
   * Dashboards/Templates tab strip so the toggle sits at the very top of the
   * explorer column.
   */
  topSlot?: ReactNode
  /**
   * Parent path for header New folder / New template. The current folder
   * when a folder is selected, otherwise the parent of the open template.
   * Root context-menu create still uses `""`.
   */
  createParentPath?: string
}

const toExplorerSelection = (selection: TemplateSelection): ExplorerSelection =>
  selection.kind === 'template' ? { kind: 'item', id: selection.id } : selection

const TemplateTreeSidebar = ({
  folders,
  templates,
  selection,
  onSelectFolder,
  onSelectTemplate,
  expanded,
  onToggle,
  onAction,
  filter,
  onFilterChange,
  isLoading,
  topSlot,
  createParentPath = ''
}: TemplateTreeSidebarProps) => {
  const folderMenu = (node: ExplorerTreeNode<TemplateFolder, Template>): ExplorerMenuEntry[] => {
    const folder = node.folder
    const displayName = folder?.name ?? node.name
    return [
      {
        icon: FolderPlus,
        label: 'New folder here',
        onSelect: () => onAction({ kind: 'create-folder', parentPath: node.path })
      },
      {
        icon: Plus,
        label: 'New template here',
        onSelect: () => onAction({ kind: 'create-template', parentPath: node.path })
      },
      {
        icon: Pencil,
        label: 'Rename',
        separatorBefore: true,
        onSelect: () =>
          onAction({ kind: 'rename-folder', path: node.path, name: displayName, folder })
      },
      {
        icon: FolderInput,
        label: 'Move to…',
        onSelect: () => onAction({ kind: 'move-folder', path: node.path, name: displayName })
      },
      {
        icon: Trash2,
        label: 'Delete',
        variant: 'destructive',
        onSelect: () =>
          onAction({ kind: 'delete-folder', path: node.path, name: displayName, folder })
      }
    ]
  }

  return (
    <ExplorerSidebar<TemplateFolder, Template>
      heading="Templates"
      ariaLabel="Template folders"
      folders={folders}
      items={templates}
      itemIcon={LayoutTemplate}
      testIdPrefix="template-tree"
      selection={toExplorerSelection(selection)}
      expanded={expanded}
      filter={filter}
      isLoading={isLoading}
      topSlot={topSlot}
      collapseStorageKey={DASHBOARDS_EXPLORER_OPEN_KEY}
      emptyHint="No templates yet. Create one to get started."
      headerButtons={[
        {
          icon: FolderPlus,
          label: 'New folder',
          onClick: () => onAction({ kind: 'create-folder', parentPath: createParentPath })
        },
        {
          icon: Plus,
          label: 'New template',
          onClick: () => onAction({ kind: 'create-template', parentPath: createParentPath })
        }
      ]}
      folderMenu={folderMenu}
      itemMenu={(template) => [
        {
          icon: Pencil,
          label: 'Rename',
          onSelect: () => onAction({ kind: 'rename-template', template })
        },
        {
          icon: FolderInput,
          label: 'Move to…',
          onSelect: () => onAction({ kind: 'move-template', template })
        },
        {
          icon: Trash2,
          label: 'Delete',
          variant: 'destructive',
          onSelect: () => onAction({ kind: 'delete-template', template })
        }
      ]}
      rootMenu={[
        {
          icon: FolderPlus,
          label: 'New folder',
          onSelect: () => onAction({ kind: 'create-folder', parentPath: '' })
        },
        {
          icon: Plus,
          label: 'New template',
          onSelect: () => onAction({ kind: 'create-template', parentPath: '' })
        }
      ]}
      onToggle={onToggle}
      onSelectFolder={onSelectFolder}
      onSelectItem={onSelectTemplate}
      onFilterChange={onFilterChange}
      onMoveItem={(template, toParentPath) => {
        onAction({ kind: 'drop-template', template, toParentPath })
      }}
      onMoveFolder={(fromPath, toParentPath) => {
        onAction({ kind: 'drop-folder', fromPath, toParentPath })
      }}
    />
  )
}

export default TemplateTreeSidebar
